// pages/Team.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Groups2Icon from "@mui/icons-material/Groups2";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getTeamImage, useTeamImageFallback } from "../utils/teamImages";
import PageShell from "../components/PageShell";
import { ACC_FILES, AC_FILES, AC_EVO_FILES } from "../results/files.config";
import { aceSessionRow } from "../utils/aceResults";
import { getLicenseColor, getLicenseLabel, computeLicensePoints } from "../utils/drivers.util";

/* =========================
   HELPERS
   ========================= */

const msToLap = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0 || ms === 2147483647 || ms === 999999999) return "—";
  const total = Math.floor(ms);
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const rem = total % 1000;
  return `${m}:${s.toString().padStart(2, "0")}.${rem.toString().padStart(3, "0")}`;
};

const __jsonPromiseCache = new Map();
export function clearTeamFetchCache() {
  __jsonPromiseCache.clear();
}

async function fetchJSONUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      console.warn("File not found:", url, res.status);
      return null;
    }

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    const isGzip = u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if (isGzip) {
      try {
        if (typeof DecompressionStream === "function") {
          const ds = new DecompressionStream("gzip");
          const stream = new Response(buf).body.pipeThrough(ds);
          buf = await new Response(stream).arrayBuffer();
          u8 = new Uint8Array(buf);
        }
      } catch (e) {
        console.error("Gzip decode failed:", url, e);
        return null;
      }
    }

    const tryDecode = (enc) => {
      try {
        return JSON.parse(new TextDecoder(enc, { fatal: false }).decode(u8));
      } catch {
        return null;
      }
    };

    return tryDecode("utf-8") || tryDecode("utf-16le") || tryDecode("utf-16be");
  } catch (e) {
    console.error("fetchJSON failed:", url, e);
    return null;
  }
}

async function fetchJSON(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJSONUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

const labelSession = (raw) => {
  const v = String(raw || "").toUpperCase();
  if (["FP", "P", "PRACTICE"].includes(v)) return "Practice";
  if (["Q", "QUALIFY", "QUALI", "QUALIFYING"].includes(v)) return "Qualifying";
  if (["R", "RACE"].includes(v)) return "Race";
  return raw || "—";
};

const cleanId = (v) => {
  if (!v) return "";
  const str = String(v).trim();
  return str.replace(/^steam:/i, "").replace(/^S/i, "");
};

const idsEqual = (a, b) => cleanId(a) === cleanId(b);

const normalizeTrack = (track) => {
  let out = track ?? "—";
  out = String(out).replaceAll("_", " ").trim();
  const words = out.split(" ").filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const slugifyTeamName = (value) => {
  const output = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/^-+|-+$/g, "");
  return output;
};

function parseACC(json, driverId, fileName) {
  const lines = json?.sessionResult?.leaderBoardLines ?? [];
  const driverIdClean = cleanId(driverId);

  const idx = lines.findIndex((l) => {
    const cur = l?.currentDriver?.playerId;
    const d0 = l?.car?.drivers?.[0]?.playerId;
    return idsEqual(cur, driverIdClean) || idsEqual(d0, driverIdClean);
  });
  if (idx < 0) return null;

  const hit = lines[idx];
  const playerId = hit?.currentDriver?.playerId || hit?.car?.drivers?.[0]?.playerId;
  const normalizedId = playerId?.startsWith("S") ? playerId : `S${cleanId(playerId)}`;

  const best = hit?.timing?.bestLap ?? null;
  const lapCount = hit?.timing?.lapCount ?? null;
  const sessionName = labelSession(json?.sessionType ?? json?.Type);

  return {
    fileName,
    sim: "ACC",
    playerId: normalizedId,
    session: sessionName,
    track: normalizeTrack(json?.trackName ?? json?.TrackName ?? json?.metaData),
    best,
    laps: Number.isFinite(lapCount) ? lapCount : null,
    position: sessionName === "Race" ? idx + 1 : null,
    date: json?.date ?? null,
  };
}

function parseAC(json, driverId, fileName) {
  const guid = cleanId(driverId);
  const laps = Array.isArray(json?.Laps) ? json.Laps : [];
  const results = Array.isArray(json?.Result) ? json.Result : [];
  const cars = Array.isArray(json?.Cars) ? json.Cars : [];

  const byLap = laps.filter((l) => cleanId(l?.DriverGuid ?? l?.Driver?.Guid) === guid);
  const inResultsIdx = results.findIndex((r) => cleanId(r?.DriverGuid) === guid);
  const inResults = inResultsIdx >= 0;
  const inCars = cars.some((c) => cleanId(c?.Driver?.Guid) === guid);

  if (!byLap.length && !inResults && !inCars) return null;

  let best = null;
  for (const l of byLap) {
    const t = l?.LapTime;
    if (Number.isFinite(t) && t > 0 && t !== 999999999) {
      best = best === null ? t : Math.min(best, t);
    }
  }

  const sessionName = labelSession(json?.Type);

  return {
    fileName,
    sim: "AC",
    session: sessionName,
    track: normalizeTrack(json?.trackName ?? json?.TrackName ?? json?.metaData),
    best,
    laps: byLap.length || null,
    position: sessionName === "Race" && inResults ? inResultsIdx + 1 : null,
  };
}

const SimChip = ({ sim }) => {
  const colorMap = {
    AC: { bg: "rgba(244, 67, 54, 0.12)", fg: "#ef5350", label: "AC" },
    ACC: { bg: "rgba(255, 193, 7, 0.12)", fg: "#fbc02d", label: "ACC" },
    AC_EVO: { bg: "rgba(0, 188, 212, 0.12)", fg: "#26c6da", label: "ACE" },
  };
  const c = colorMap[sim] || { bg: "action.hover", fg: "text.primary", label: sim || "—" };
  return (
    <Chip
      label={c.label}
      size="small"
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: 700,
        borderRadius: 1.5,
      }}
    />
  );
};

const SessionChip = ({ session }) => {
  const v = (session || "").toLowerCase();
  const palette = {
    practice: { bg: "rgba(33,150,243,.12)", fg: "#2196f3" },
    qualifying: { bg: "rgba(156,39,176,.12)", fg: "#9c27b0" },
    race: { bg: "rgba(76,175,80,.12)", fg: "#4caf50" },
  };
  const c =
    v.includes("race") ? palette.race : v.includes("qual") ? palette.qualifying : palette.practice;

  return (
    <Chip
      label={session || "—"}
      size="small"
      sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, borderRadius: 1.5 }}
    />
  );
};

const StatBlock = ({ label, value, sub }) => (
  <Box sx={{ width: "100%", p: 2, borderRadius: 3, textAlign: "center", margin: "auto" }}>
    <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
      {sub ? ` · ${sub}` : ""}
    </Typography>
  </Box>
);

const SmallStat = ({ icon, label, value, color }) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 3,
      height: "100%",
      background: "linear-gradient(135deg, rgba(255,255,255,.03), rgba(255,255,255,.06))",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Avatar
        sx={{
          bgcolor: color ? `${color}22` : "action.hover",
          color: color || "text.primary",
          width: 42,
          height: 42,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

function SessionRowCard({ r, lapMax, onClick }) {
  const laps = Number.isFinite(r?.laps) ? r.laps : null;
  const pct = laps ? Math.max(4, Math.round((laps / lapMax) * 100)) : 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        px: 1.5,
        py: 1.25,
        "&:active": { opacity: 0.85 },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap">
            <Typography sx={{ fontWeight: 900 }} noWrap>
              {r.track}
            </Typography>
            <SimChip sim={r.sim} />
            <SessionChip session={r.session} />
            {r.driverName ? <Chip label={r.driverName} size="small" variant="outlined" /> : null}
          </Stack>

          <Typography variant="body2" color="text.secondary" noWrap>
            Best {msToLap(r.best)}{" "}
            {Number.isFinite(r?.position) ? `• P${r.position}` : ""}{" "}
            {laps ? `• ${laps} laps` : ""}
          </Typography>

          {laps ? (
            <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 8,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${pct}%`,
                    bgcolor: "primary.main",
                    opacity: 0.65,
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function computeRaceStatsFromRows(rows) {
  const raceRows = rows.filter((r) => (r.session || "").toLowerCase() === "race");
  const ordered = [...raceRows].reverse();

  const finishes = raceRows.length;
  const podiums = raceRows.reduce(
    (acc, r) => (Number.isFinite(r?.position) && r.position <= 3 ? acc + 1 : acc),
    0
  );
  const wins = raceRows.reduce((acc, r) => (r.position === 1 ? acc + 1 : acc), 0);
  const positions = ordered.map((r) => r?.position).filter((v) => Number.isFinite(v));
  const bestPos = positions.length ? Math.min(...positions) : null;
  const avgPos = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : null;
  const totalRaceLaps = ordered.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);
  const uniqueTracks = new Set(ordered.map((r) => (r?.track || "").trim()).filter(Boolean)).size;

  return {
    finishes,
    podiums,
    wins,
    bestPos,
    avgPos,
    totalRaceLaps,
    uniqueTracks,
  };
}

function bestOfRows(rows) {
  let best = null;
  let meta = null;

  for (const r of rows) {
    if (
      Number.isFinite(r?.best) &&
      r.best > 0 &&
      r.best !== 999999999 &&
      r.best !== 2147483647
    ) {
      if (best === null || r.best < best) {
        best = r.best;
        meta = r;
      }
    }
  }

  return best !== null ? { time: best, meta } : null;
}

function getACCDateString(f) {
  const fullDateList = f.split("/")[1].split("_");
  return fullDateList[0] + fullDateList[1];
}

function getACDateString(f) {
  let fullDateList = f.split("/")[1];
  fullDateList = fullDateList.split(".")[0].split("_");
  return (
    fullDateList[0] +
    fullDateList[1] +
    fullDateList[2] +
    fullDateList[3] +
    fullDateList[4]
  );
}

/* =========================
   MAIN
   ========================= */

export default function Team({ data }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const teamSlug = String(id || "").trim().toLowerCase();

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [simTab, setSimTab] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [teamRows, setTeamRows] = useState([]);
  const [driverRowsMap, setDriverRowsMap] = useState({});

  const allDrivers = data?.drivers?.list ?? [];

  const teamDrivers = useMemo(() => {
    if (!teamSlug) return [];
    return allDrivers.filter((d) => slugifyTeamName(d?.team) === slugifyTeamName(teamSlug));
  }, [allDrivers, teamSlug]);

  const resolvedTeamName = useMemo(() => {
    if (teamDrivers[0]?.team) return teamDrivers[0].team;
    return "";
  }, [teamDrivers]);

  useEffect(() => {
    if (!teamDrivers.length) {
      setTeamRows([]);
      setDriverRowsMap({});
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoading(true);

      const toUrl = (f) =>
        process.env.PUBLIC_URL
          ? `${process.env.PUBLIC_URL}${f.startsWith("/") ? "" : "/"}${f}`
          : `${f.startsWith("/") ? "" : "/"}${f}`;

      const sortedAccFiles = [...ACC_FILES].sort(
        (a, b) => getACCDateString(a) - getACCDateString(b)
      );
      const sortedAcFiles = [...AC_FILES].sort(
        (a, b) => getACDateString(a) - getACDateString(b)
      );

      const [accList, acList, aceList] = await Promise.all([
        Promise.all(
          sortedAccFiles.map((f) => fetchJSON(toUrl(f)))
        ),
        Promise.all(
          sortedAcFiles.map((f) => fetchJSON(toUrl(f)))
        ),
        Promise.all(AC_EVO_FILES.map((f) => fetchJSON(toUrl(f)))),
      ]);

      const accJsons = accList.filter(Boolean);
      const acJsons = acList.filter(Boolean);
      const aceJsons = aceList.filter(Boolean);

      const nextDriverRowsMap = {};
      const merged = [];

      for (const d of teamDrivers) {
        const dAcc = accJsons
          .map((j, i) => parseACC(j, d.id, sortedAccFiles[i]))
          .filter(Boolean)
          .map((r) => ({
            ...r,
            driverId: d.id,
            driverName: d.name,
            driverNumber: d.number,
            driverImage: d.image,
            team: d.team,
            car: d.car,
          }));

        const dAc = acJsons
          .map((j, i) => parseAC(j, d.id, sortedAcFiles[i]))
          .filter(Boolean)
          .map((r) => ({
            ...r,
            driverId: d.id,
            driverName: d.name,
            driverNumber: d.number,
            driverImage: d.image,
            team: d.team,
            car: d.car,
          }));

        const dAce = aceJsons
          .map((j, i) => aceSessionRow(j, d.id, AC_EVO_FILES[i]))
          .filter(Boolean)
          .map((r) => ({
            ...r,
            driverId: d.id,
            driverName: d.name,
            driverNumber: d.number,
            driverImage: d.image,
            team: d.team,
            car: d.car,
          }));

        const rows = [...dAc, ...dAcc, ...dAce].reverse();
        nextDriverRowsMap[String(d.id)] = rows;
        merged.push(...rows);
      }

      if (!alive) return;

      setDriverRowsMap(nextDriverRowsMap);
      setTeamRows(merged);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [teamDrivers]);

  const filteredRows = useMemo(() => {
    let list = [...teamRows];

    if (simTab === "ac") list = list.filter((r) => r.sim === "AC");
    if (simTab === "acc") list = list.filter((r) => r.sim === "ACC");
    if (simTab === "ace") list = list.filter((r) => r.sim === "AC_EVO");

    if (sessionFilter !== "all") {
      list = list.filter(
        (r) => (r.session || "").toLowerCase() === sessionFilter.toLowerCase()
      );
    }

    return list;
  }, [teamRows, simTab, sessionFilter]);

  const lapMax = useMemo(() => {
    let m = 0;
    for (const r of filteredRows) if (Number.isFinite(r?.laps)) m = Math.max(m, r.laps);
    return Math.max(m, 1);
  }, [filteredRows]);

  const teamRaceStats = useMemo(() => computeRaceStatsFromRows(teamRows), [teamRows]);

  const teamStats = useMemo(() => {
    const acRows = teamRows.filter((r) => r.sim === "AC");
    const accRows = teamRows.filter((r) => r.sim === "ACC");

    const raceOnly = teamRows.filter((r) => r.session === "Race");
    const acRaceOnly = acRows.filter((r) => r.session === "Race");
    const accRaceOnly = accRows.filter((r) => r.session === "Race");

    const totalLaps = raceOnly.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);
    const acLaps = acRaceOnly.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);
    const accLaps = accRaceOnly.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);

    const pbAll = bestOfRows(raceOnly);
    const pbAC = bestOfRows(acRaceOnly);
    const pbACC = bestOfRows(accRaceOnly);

    const avgGrid =
      teamDrivers.length > 0
        ? Number(
            (
              teamDrivers.reduce((a, d) => {
                const dRows = driverRowsMap[String(d.id)] || [];
                const dRaceStats = computeRaceStatsFromRows(dRows);
                return a + (Number.isFinite(dRaceStats.avgPos) ? dRaceStats.avgPos : 0);
              }, 0) /
                (teamDrivers.filter((d) => {
                  const dRows = driverRowsMap[String(d.id)] || [];
                  return Number.isFinite(computeRaceStatsFromRows(dRows).avgPos);
                }).length || 1)
            ).toFixed(2)
          )
        : null;

    return {
      sessionsTotal: raceOnly.length,
      sessionsAC: acRaceOnly.length,
      sessionsACC: accRaceOnly.length,
      lapsTotal: totalLaps,
      lapsAC: acLaps,
      lapsACC: accLaps,
      pbAll,
      pbAC,
      pbACC,
      avgGrid,
    };
  }, [teamRows, driverRowsMap, teamDrivers]);

  const teamRosterStats = useMemo(() => {
    return teamDrivers
      .map((d) => {
        const rows = driverRowsMap[String(d.id)] || [];
        const raceStats = computeRaceStatsFromRows(rows);
        const pb = bestOfRows(rows.filter((r) => r.session === "Race"));

        return {
          ...d,
          finishes: raceStats.finishes,
          wins: raceStats.wins,
          podiums: raceStats.podiums,
          bestPos: raceStats.bestPos,
          avgPos: raceStats.avgPos,
          totalRaceLaps: raceStats.totalRaceLaps,
          uniqueTracks: raceStats.uniqueTracks,
          pb,
        };
      })
      .sort((a, b) => {
        if ((b.wins || 0) !== (a.wins || 0)) return (b.wins || 0) - (a.wins || 0);
        if ((b.podiums || 0) !== (a.podiums || 0)) return (b.podiums || 0) - (a.podiums || 0);
        if ((b.finishes || 0) !== (a.finishes || 0)) return (b.finishes || 0) - (a.finishes || 0);
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [teamDrivers, driverRowsMap]);

  const heroImage = useMemo(() => {
    return getTeamImage(resolvedTeamName);
  }, [resolvedTeamName]);

  const bestDriverByWins = useMemo(() => {
    if (!teamRosterStats.length) return null;
    return [...teamRosterStats].sort((a, b) => (b.wins || 0) - (a.wins || 0))[0];
  }, [teamRosterStats]);

  const bestDriverByPB = useMemo(() => {
    const withPb = teamRosterStats.filter((d) => Number.isFinite(d?.pb?.time));
    if (!withPb.length) return null;
    return [...withPb].sort((a, b) => a.pb.time - b.pb.time)[0];
  }, [teamRosterStats]);

  const handleGoBack = () => window.history.back();

  if (!teamSlug) {
    return (
      <PageShell title="Team" bannerImage="/f1.png">
        <Container sx={{ py: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            No team selected
          </Typography>
          <Button onClick={handleGoBack} variant="contained">
            Back
          </Button>
        </Container>
      </PageShell>
    );
  }

  if (!loading && !teamDrivers.length) {
    return (
      <PageShell title="Team" bannerImage="/f1.png">
        <Container sx={{ py: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Team not found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No drivers matched team id “{teamSlug}”.
          </Typography>
          <Button onClick={handleGoBack} variant="contained">
            Back
          </Button>
        </Container>
      </PageShell>
    );
  }

  return (
    <PageShell title={resolvedTeamName || "Team"} bannerImage="/f1.png">
      <Container sx={{ py: 2 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.07))",
            backdropFilter: "blur(2px)",
            overflow: "hidden",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto" alignContent="center" sx={{display: "flex", margin: "auto"}}>
              <img
                src={heroImage}
                onError={useTeamImageFallback}
                alt={resolvedTeamName}
                variant="square"
                style={{ 
                  width: "70%",
                  height: { xs: 120, md: 220 },
                  margin: "auto",
                  display: "block",
                  borderRadius: 3,
                  mx: { xs: "auto", md: 0 }
                }}
              />
            </Grid>

            <Grid item xs>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {resolvedTeamName}
                  </Typography>
                  <Chip label={`${teamDrivers.length} driver${teamDrivers.length === 1 ? "" : "s"}`} />
                  <Chip
                    label={`${teamRaceStats.finishes || 0} race finish${teamRaceStats.finishes === 1 ? "" : "es"}`}
                    color="primary"
                  />
                </Stack>

                <Typography variant="body1" color="text.secondary">
                  Team overview with combined race results, roster performance, personal bests, wins,
                  podiums, average finishing positions, and recent sessions.
                </Typography>

                <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <SmallStat
                      icon={<MilitaryTechIcon />}
                      label="Wins"
                      value={teamRaceStats.wins || 0}
                      color="#ffd54f"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SmallStat
                      icon={<EmojiEventsIcon />}
                      label="Podiums"
                      value={teamRaceStats.podiums || 0}
                      color="#ffb300"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SmallStat
                      icon={<SportsScoreIcon />}
                      label="Best Finish"
                      value={
                        Number.isFinite(teamRaceStats.bestPos)
                          ? `P${teamRaceStats.bestPos}`
                          : "—"
                      }
                      color="#66bb6a"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SmallStat
                      icon={<Groups2Icon />}
                      label="Avg Finish"
                      value={
                        Number.isFinite(teamRaceStats.avgPos)
                          ? `P${teamRaceStats.avgPos.toFixed(2)}`
                          : "—"
                      }
                      color="#42a5f5"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ pt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Team activity
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(8, (teamRaceStats.totalRaceLaps || 0) / 5))}
                    sx={{
                      mt: 0.75,
                      height: 10,
                      borderRadius: 99,
                      bgcolor: "action.hover",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    {teamRaceStats.totalRaceLaps || 0} race laps across {teamRaceStats.uniqueTracks || 0} tracks
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item sx={{ width: { xs: "100%", lg: "31%" } }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Overall
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Races" value={teamStats.sessionsTotal} sub={`${teamStats.lapsTotal} laps`} />
                <StatBlock
                  label="Team PB"
                  value={teamStats.pbAll ? msToLap(teamStats.pbAll.time) : "—"}
                  sub={teamStats.pbAll?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item sx={{ width: { xs: "100%", lg: "31%" } }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Assetto Corsa
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Races" value={teamStats.sessionsAC} sub={`${teamStats.lapsAC} laps`} />
                <StatBlock
                  label="PB (AC)"
                  value={teamStats.pbAC ? msToLap(teamStats.pbAC.time) : "—"}
                  sub={teamStats.pbAC?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item sx={{ width: { xs: "100%", lg: "31%" } }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Assetto Corsa Competizione
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Races" value={teamStats.sessionsACC} sub={`${teamStats.lapsACC} laps`} />
                <StatBlock
                  label="PB (ACC)"
                  value={teamStats.pbACC ? msToLap(teamStats.pbACC.time) : "—"}
                  sub={teamStats.pbACC?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Winningest Driver
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {bestDriverByWins ? bestDriverByWins.name : "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bestDriverByWins ? `${bestDriverByWins.wins || 0} wins` : "No race wins yet"}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Fastest Driver PB
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {bestDriverByPB ? bestDriverByPB.name : "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bestDriverByPB?.pb ? msToLap(bestDriverByPB.pb.time) : "No PB available"}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Team Average Finish
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {Number.isFinite(teamStats.avgGrid) ? `P${teamStats.avgGrid}` : "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average of each driver’s race average
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            mt: 3,
            background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              mb: 2,
              "& .MuiTab-root": { minHeight: 36, fontWeight: 700 },
            }}
          >
            <Tab value="overview" label="Overview" />
            <Tab value="drivers" label={`Drivers (${teamDrivers.length})`} />
            <Tab value="sessions" label={`Sessions (${teamRows.length})`} />
          </Tabs>

          {tab === "overview" && (
            <Grid container spacing={2}>
              {teamRosterStats.map((driver) => (
                <Grid key={driver.id} item xs={12} md={6} xl={4}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: "100%",
                      cursor: "pointer",
                      transition: "transform .15s ease, box-shadow .15s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => navigate(`/driver/${driver.id}`)}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar src={driver.image} sx={{ width: 64, height: 64, borderRadius: 2 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
                          {driver.name}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip size="small" label={`#${driver.number}`} />
                          {driver.license ? (
                            <Chip
                              size="small"
                              label={getLicenseLabel(computeLicensePoints(driver))}
                              sx={{
                                backgroundColor: getLicenseColor(getLicenseLabel(computeLicensePoints(driver))),
                                color: "#fff",
                              }}
                            />
                          ) : null}
                        </Stack>
                      </Box>
                    </Stack>

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <SmallStat icon={<MilitaryTechIcon />} label="Wins" value={driver.wins || 0} color="#ffd54f" />
                      </Grid>
                      <Grid item xs={6}>
                        <SmallStat icon={<EmojiEventsIcon />} label="Podiums" value={driver.podiums || 0} color="#ffb300" />
                      </Grid>
                      <Grid item xs={6}>
                        <SmallStat
                          icon={<SportsScoreIcon />}
                          label="Best Finish"
                          value={Number.isFinite(driver.bestPos) ? `P${driver.bestPos}` : "—"}
                          color="#66bb6a"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <SmallStat
                          icon={<Groups2Icon />}
                          label="Avg Finish"
                          value={Number.isFinite(driver.avgPos) ? `P${driver.avgPos.toFixed(2)}` : "—"}
                          color="#42a5f5"
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="body2" color="text.secondary">
                      PB: <strong>{driver.pb ? msToLap(driver.pb.time) : "—"}</strong>
                      {driver.pb?.meta?.track ? ` • ${driver.pb.meta.track}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {driver.finishes || 0} races • {driver.totalRaceLaps || 0} laps • {driver.uniqueTracks || 0} tracks
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {tab === "drivers" && (
            <List disablePadding>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <Paper key={i} sx={{ p: 2, mb: 1.5, borderRadius: 3 }}>
                    <Skeleton width="35%" />
                    <Skeleton width="60%" />
                    <Skeleton width="45%" />
                  </Paper>
                ))
              ) : (
                teamRosterStats.map((driver, i) => (
                  <Paper key={driver.id} sx={{ mb: 1.5, borderRadius: 3, overflow: "hidden" }}>
                    <ListItem
                      sx={{ cursor: "pointer", alignItems: "flex-start" }}
                      onClick={() => navigate(`/driver/${driver.id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar src={driver.image} sx={{ width: 64, height: 64, borderRadius: 2 }} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontWeight: 900 }}>{driver.name}</Typography>
                            <Chip size="small" label={`#${driver.number}`} />
                            <Chip
                              size="small"
                              label={getLicenseLabel(computeLicensePoints(driver))}
                              sx={{
                                backgroundColor: getLicenseColor(getLicenseLabel(computeLicensePoints(driver))),
                                color: "#fff",
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Car: {driver.car || "—"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              PB: {driver.pb ? msToLap(driver.pb.time) : "—"}
                              {driver.pb?.meta?.track ? ` • ${driver.pb.meta.track}` : ""}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Races {driver.finishes || 0} • Wins {driver.wins || 0} • Podiums {driver.podiums || 0} •
                              Best {Number.isFinite(driver.bestPos) ? ` P${driver.bestPos}` : " —"} •
                              Avg {Number.isFinite(driver.avgPos) ? ` P${driver.avgPos.toFixed(2)}` : " —"}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {i !== teamRosterStats.length - 1 ? <Divider /> : null}
                  </Paper>
                ))
              )}
            </List>
          )}

          {tab === "sessions" && (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Tabs
                  value={simTab}
                  onChange={(_, v) => setSimTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": { minHeight: 36, fontWeight: 700 },
                  }}
                >
                  <Tab value="all" label="All" />
                  <Tab value="acc" label="ACC" />
                  <Tab value="ace" label="ACE" />
                  <Tab value="ac" label="AC" />
                </Tabs>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {["all", "Practice", "Qualifying", "Race"].map((s) => {
                    const active = sessionFilter === s;
                    return (
                      <Chip
                        key={s}
                        label={s}
                        onClick={() => setSessionFilter(s)}
                        color={active ? "primary" : "default"}
                        variant={active ? "filled" : "outlined"}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      />
                    );
                  })}
                </Stack>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {filteredRows.length} sessions •{" "}
                {filteredRows.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0)} laps shown
              </Typography>

              <Box sx={{ display: { xs: "block", md: "none" }, mt: 1.5 }}>
                <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
                  {loading ? (
                    <Box sx={{ p: 1.5 }}>
                      {[...Array(6)].map((_, i) => (
                        <Box key={`msk-${i}`} sx={{ mb: 1.25 }}>
                          <Skeleton width="60%" />
                          <Skeleton width="85%" />
                        </Box>
                      ))}
                    </Box>
                  ) : filteredRows.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No sessions match the current filters.
                      </Typography>
                    </Box>
                  ) : (
                    filteredRows.map((r, i) => (
                      <Box key={`mrow-${i}`}>
                        <SessionRowCard r={r} lapMax={lapMax} onClick={() => navigate(`/results?session=${encodeURIComponent(r.fileName)}`)} />
                        {i !== filteredRows.length - 1 && <Divider />}
                      </Box>
                    ))
                  )}
                </Paper>
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer sx={{ mt: 1.5, maxHeight: 520, borderRadius: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Driver</TableCell>
                        <TableCell>Sim</TableCell>
                        <TableCell>Track</TableCell>
                        <TableCell>Session</TableCell>
                        <TableCell>Best Lap</TableCell>
                        <TableCell align="right">Position</TableCell>
                        <TableCell width={220}>Lap Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && (
                        <>
                          {[...Array(5)].map((_, i) => (
                            <TableRow key={`sk-${i}`}>
                              <TableCell><Skeleton width={120} /></TableCell>
                              <TableCell><Skeleton width={28} /></TableCell>
                              <TableCell><Skeleton width={120} /></TableCell>
                              <TableCell><Skeleton width={90} /></TableCell>
                              <TableCell><Skeleton width={80} /></TableCell>
                              <TableCell align="right"><Skeleton width={36} /></TableCell>
                              <TableCell><Skeleton width={180} /></TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {!loading && filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No sessions match the current filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading &&
                        filteredRows.map((r, i) => {
                          const laps = Number.isFinite(r?.laps) ? r.laps : null;
                          const pct = laps ? Math.max(4, Math.round((laps / lapMax) * 100)) : 0;

                          return (
                            <TableRow
                              key={`${r.driverId}-${r.sim}-${r.track}-${r.session}-${i}`}
                              hover
                              onClick={() => navigate(`/results?session=${encodeURIComponent(r.fileName)}`)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Avatar src={r.driverImage} sx={{ width: 28, height: 28 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {r.driverName}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell><SimChip sim={r.sim} /></TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {r.track}
                                </Typography>
                              </TableCell>
                              <TableCell><SessionChip session={r.session} /></TableCell>
                              <TableCell>
                                <Typography variant="body2">{msToLap(r.best)}</Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color:
                                    r.position === 1
                                      ? getLicenseColor("gold")
                                      : r.position === 2
                                      ? getLicenseColor("silver")
                                      : r.position === 3
                                      ? getLicenseColor("bronze")
                                      : "text.primary",
                                }}
                              >
                                {Number.isFinite(r?.position) ? r.position : "—"}
                              </TableCell>
                              <TableCell>
                                {laps ? (
                                  <Tooltip title={`${laps} laps`}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Box
                                        sx={{
                                          position: "relative",
                                          width: "100%",
                                          height: 10,
                                          bgcolor: "action.hover",
                                          borderRadius: 1,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: `${pct}%`,
                                            bgcolor: "primary.main",
                                            opacity: 0.7,
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ minWidth: 24, textAlign: "right" }}
                                      >
                                        {laps}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Paper>

        <Stack direction="row" sx={{ mt: 3 }}>
          <Button onClick={handleGoBack} variant="contained" startIcon={<CheckCircleIcon />}>
            Back
          </Button>
        </Stack>
      </Container>
    </PageShell>
  );
}
